/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  isLocalStorageSupported,
  loadFromLocalStorage,
  saveToLocalStorage,
} from "../utils/storageUtils";
import { useLanguage } from "./LanguageContext";
import { useStartDate } from "./StartDateContext";

// Define types for our context
type SalatType = {
  name: string;
  farad: boolean;
  sunnah: boolean;
  nafal: boolean;
};

type ChecklistItem = {
  id: number;
  text: string;
  completed: boolean;
};

type DailyTask = {
  id: number;
  name: string;
  completed: boolean;
};

type QuranTracker = {
  ayat: string;
  page: string;
  para: string;
};

// Add timestamp information to the RamadanDay type
type RamadanDay = {
  day: number;
  salats: SalatType[];
  quranTracker: QuranTracker;
  checklist: ChecklistItem[];
  dailyTasks: DailyTask[];
  specialAchievement: string;
  timestamp: {
    year: number;
    month: number;
    day: number;
    lastUpdated: number; // Unix timestamp
  };
};

type RamadanContextType = {
  currentDay: number;
  setCurrentDay: (day: number) => void;
  dayData: RamadanDay | null;
  updateSalat: (
    name: string,
    type: "farad" | "sunnah" | "nafal",
    value: boolean
  ) => void;
  updateQuranTracker: (field: "ayat" | "page" | "para", value: string) => void;
  updateChecklist: (id: number, completed: boolean) => void;
  updateDailyTask: (id: number, completed: boolean) => void;
  updateSpecialAchievement: (text: string) => void;
  getSummaryData: () => {
    totalDays: number;
    completedPrayers: number;
    totalPrayers: number;
    completedTasks: number;
    totalTasks: number;
    specialAchievements: Record<string, string>;
  };
  isMenuVisible: boolean;
  toggleMenuVisibility: () => void;
  isSummaryVisible: boolean;
  toggleSummaryVisibility: () => void;
};

const RamadanContext = createContext<RamadanContextType | undefined>(undefined);

type RamadanProviderProps = {
  children: ReactNode;
};

// Generate a storage key with year information
const getStorageKey = () => {
  const now = new Date();
  return `ramadanData_${now.getFullYear()}`;
};

export const RamadanProvider: React.FC<RamadanProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const startDateContext = useStartDate();
  const [currentDay, setCurrentDay] = useState<number>(1);
  const [days, setDays] = useState<{ [key: number]: RamadanDay }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasLocalStorage, setHasLocalStorage] = useState(true);
  const [isMenuVisible, setIsMenuVisible] = useState(true);
  const [isSummaryVisible, setIsSummaryVisible] = useState(true);

  // Update currentDay when currentRamadanDay changes in StartDateContext
  useEffect(() => {
    if (startDateContext && startDateContext.isDateSelected) {
      setCurrentDay(startDateContext.currentRamadanDay);
    }
  }, [startDateContext]);

  // Sample initial data structure
  const getInitialDayData = useCallback(
    (day: number): RamadanDay => {
      const now = new Date();

      return {
        day,
        salats: [
          { name: t("salats.fajr"), farad: false, sunnah: false, nafal: false },
          { name: t("salats.zuhr"), farad: false, sunnah: false, nafal: false },
          { name: t("salats.asr"), farad: false, sunnah: false, nafal: false },
          {
            name: t("salats.maghrib"),
            farad: false,
            sunnah: false,
            nafal: false,
          },
          { name: t("salats.isha"), farad: false, sunnah: false, nafal: false },
          {
            name: t("salats.tarawih"),
            farad: false,
            sunnah: false,
            nafal: false,
          },
          { name: t("salats.duha"), farad: false, sunnah: false, nafal: false },
          {
            name: t("salats.dukaul"),
            farad: false,
            sunnah: false,
            nafal: false,
          },
        ],
        quranTracker: {
          ayat: "",
          page: "",
          para: "",
        },
        checklist: [
          { id: 1, text: t("checklist.morningZikir"), completed: false },
          { id: 2, text: t("checklist.eveningZikir"), completed: false },
          { id: 3, text: t("checklist.danSadaqa"), completed: false },
          { id: 4, text: t("checklist.dailyTask"), completed: false },
          {
            id: 5,
            text: t("checklist.istigfarAtLeast70Times"),
            completed: false,
          },
          { id: 6, text: t("checklist.quranTilawat"), completed: false },
          { id: 7, text: t("checklist.sleepZikir"), completed: false },
          { id: 8, text: t("checklist.memorizeDua"), completed: false },
          {
            id: 9,
            text: t("checklist.memorizeNamesOfAllah"),
            completed: false,
          },
          { id: 10, text: t("checklist.jamatSalat"), completed: false },
          { id: 11, text: t("checklist.newKnowledge"), completed: false },
          { id: 12, text: t("checklist.hadith"), completed: false },
        ],
        dailyTasks: [
          {
            id: 1,
            name: t("dailyTasks.beforeSalat"),
            completed: false,
          },
        ],
        specialAchievement: "",
        // Add timestamp information
        timestamp: {
          year: now.getFullYear(),
          month: now.getMonth() + 1, // JavaScript months are 0-based
          day: now.getDate(),
          lastUpdated: Date.now(),
        },
      };
    },
    [t]
  );

  // Utility function to refresh translations in existing data
  const refreshTranslations = useCallback(() => {
    if (Object.keys(days).length === 0) return;
    
    setDays((prevDays) => {
      const updatedDays = { ...prevDays };
      
      // Update translations for each day's data
      Object.keys(updatedDays).forEach((dayKey) => {
        const day = updatedDays[Number(dayKey)];
        
        // Update salat names
        day.salats = day.salats.map((salat, index) => ({
          ...salat,
          name: t(`salats.${["fajr", "zuhr", "asr", "maghrib", "isha", "tarawih", "duha", "dukaul"][index]}`),
        }));
        
        // Update checklist texts
        day.checklist = day.checklist.map((item) => ({
          ...item,
          text: t(`checklist.${[
            "morningZikir", 
            "eveningZikir", 
            "danSadaqa", 
            "dailyTask", 
            "istigfarAtLeast70Times", 
            "quranTilawat", 
            "sleepZikir", 
            "memorizeDua", 
            "memorizeNamesOfAllah", 
            "jamatSalat", 
            "newKnowledge", 
            "hadith"
          ][item.id - 1]}`),
        }));
        
        // Update daily tasks
        day.dailyTasks = day.dailyTasks.map((task) => ({
          ...task,
          name: t("dailyTasks.beforeSalat"),
        }));
      });
      
      return updatedDays;
    });
  }, [t]);

  // Check localStorage support
  useEffect(() => {
    const supported = isLocalStorageSupported();
    setHasLocalStorage(supported);
    if (!supported) {
      console.warn(
        "localStorage is not supported in this browser. Data will not persist between sessions."
      );
    }
  }, []);

  // Load data from localStorage on initial render
  useEffect(() => {
    if (hasLocalStorage) {
      const storageKey = getStorageKey();
      const savedData = loadFromLocalStorage<{ [key: number]: RamadanDay }>(
        storageKey,
        {}
      );
      setDays(savedData);
      setIsInitialized(true);
    }
  }, [hasLocalStorage]);

  // Update translations when language changes
  useEffect(() => {
    if (isInitialized) {
      refreshTranslations();
    }
  }, [language, isInitialized, refreshTranslations]);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    if (isInitialized && hasLocalStorage) {
      const storageKey = getStorageKey();
      saveToLocalStorage(storageKey, days);
    }
  }, [days, isInitialized, hasLocalStorage]);

  // Get current day data, initialize if not found
  const [dayData, setDayData] = useState<RamadanDay | null>(null);

  // Update timestamp for the day
  const updateTimestamp = () => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      lastUpdated: Date.now(),
    };
  };

  // Update salat state
  const updateSalat = useCallback(
    (name: string, type: "farad" | "sunnah" | "nafal", value: boolean) => {
      setDays((prevDays) => {
        const currentDayData =
          prevDays[currentDay] || getInitialDayData(currentDay);

      const updatedSalats = currentDayData.salats.map((salat) => {
        if (salat.name === name) {
          return { ...salat, [type]: value };
        }
        return salat;
      });

      return {
        ...prevDays,
        [currentDay]: {
          ...currentDayData,
          salats: updatedSalats,
          timestamp: updateTimestamp(),
        },
      };
    });
  }, [currentDay, getInitialDayData]);

  // Update Quran tracker
  const updateQuranTracker = useCallback(
    (field: "ayat" | "page" | "para", value: string) => {
    setDays((prevDays) => {
      const currentDayData =
        prevDays[currentDay] || getInitialDayData(currentDay);

      return {
        ...prevDays,
        [currentDay]: {
          ...currentDayData,
          quranTracker: {
            ...currentDayData.quranTracker,
            [field]: value,
          },
          timestamp: updateTimestamp(),
        },
      };
    });
  }, [currentDay, getInitialDayData]);

  // Update checklist item
  const updateChecklist = useCallback(
    (id: number, completed: boolean) => {
      setDays((prevDays) => {
      const currentDayData =
        prevDays[currentDay] || getInitialDayData(currentDay);

      const updatedChecklist = currentDayData.checklist.map((item) => {
        if (item.id === id) {
          return { ...item, completed };
        }
        return item;
      });

      return {
        ...prevDays,
        [currentDay]: {
          ...currentDayData,
          checklist: updatedChecklist,
          timestamp: updateTimestamp(),
        },
      };
    });
  }, [currentDay, getInitialDayData]);

  // Update daily task
  const updateDailyTask = useCallback(
    (id: number, completed: boolean) => {
      setDays((prevDays) => {
      const currentDayData =
        prevDays[currentDay] || getInitialDayData(currentDay);

      const updatedTasks = currentDayData.dailyTasks.map((task) => {
        if (task.id === id) {
          return { ...task, completed };
        }
        return task;
      });

      return {
        ...prevDays,
        [currentDay]: {
          ...currentDayData,
          dailyTasks: updatedTasks,
          timestamp: updateTimestamp(),
        },
      };
      });
  }, [currentDay, getInitialDayData]);

  // Update special achievement
  const updateSpecialAchievement = useCallback(
    (text: string) => {
      setDays((prevDays) => {
      const currentDayData =
        prevDays[currentDay] || getInitialDayData(currentDay);

      return {
        ...prevDays,
        [currentDay]: {
          ...currentDayData,
          specialAchievement: text,
          timestamp: updateTimestamp(),
        },
      };
    });
  }, [currentDay, getInitialDayData]);

  // Get summary data for all days
  const getSummaryData = useCallback(() => {
    const totalDays = Object.keys(days).length;
    let completedPrayers = 0;
    let totalPrayers = 0;
    let completedTasks = 0;
    let totalTasks = 0;
    const specialAchievements: Record<string, string> = {};

    Object.values(days).forEach((day) => {
      // Count prayers
      day.salats.forEach((salat) => {
        if (salat.farad) {
          completedPrayers++;
          totalPrayers++;
        } else {
          totalPrayers++;
        }
        if (salat.sunnah) {
          completedPrayers++;
          totalPrayers++;
        } else {
          totalPrayers++;
        }
        if (salat.nafal) {
          completedPrayers++;
          totalPrayers++;
        } else {
          totalPrayers++;
        }
      });

      // Count checklist items
      day.checklist.forEach((item) => {
        if (item.completed) {
          completedTasks++;
        }
        totalTasks++;
      });

      // Count daily tasks
      day.dailyTasks.forEach((task) => {
        if (task.completed) {
          completedTasks++;
        }
        totalTasks++;
      });
    });

    Object.values(days).forEach((day) => {
      if (day.specialAchievement) {
        specialAchievements[day.day] = day.specialAchievement;
      }
    });

    return {
      totalDays,
      completedPrayers,
      totalPrayers,
      completedTasks,
      totalTasks,
      specialAchievements,
    };
  }, [days]);

  // Toggle menu visibility
  const toggleMenuVisibility = useCallback(() => {
    setIsMenuVisible((prev) => !prev);
  }, []);

  // Toggle summary visibility
  const toggleSummaryVisibility = useCallback(() => {
    setIsSummaryVisible((prev) => !prev);
  }, []);

  // Update dayData whenever currentDay or days changes
  useEffect(() => {
    // Only update dayData if days[currentDay] is different from current dayData
    const newDayData = days[currentDay] || getInitialDayData(currentDay);
    setDayData(newDayData);
  }, [currentDay, days, getInitialDayData]);

  // Create memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      currentDay,
      setCurrentDay,
      dayData,
      updateSalat,
      updateQuranTracker,
      updateChecklist,
      updateDailyTask,
      updateSpecialAchievement,
      getSummaryData,
      isMenuVisible,
      toggleMenuVisibility,
      isSummaryVisible,
      toggleSummaryVisibility,
    }),
    [
      currentDay,
      dayData,
      isMenuVisible,
      isSummaryVisible,
      // Include these functions in the dependency array to prevent stale closures
      updateSalat,
      updateQuranTracker, 
      updateChecklist,
      updateDailyTask,
      updateSpecialAchievement,
      getSummaryData,
      toggleMenuVisibility,
      toggleSummaryVisibility
    ]
  );

  return (
    <RamadanContext.Provider value={contextValue}>
      {children}
    </RamadanContext.Provider>
  );
};

// Custom hook to use the Ramadan context
export const useRamadan = () => {
  const context = useContext(RamadanContext);

  if (context === undefined) {
    throw new Error("useRamadan must be used within a RamadanProvider");
  }

  return context;
};
